import { ref } from 'vue';
import {
  getCatalogueItemCustomFields,
  type CatalogueCustomFieldMeta,
} from '@/api/CatalogueApi';
import {
  CatalogueFilterType,
  CatalogueFilterOp,
  CatalogueFilterGroup,
  TEXT_OPS,
  NUMBER_OPS,
  type CatalogueFilterDefinition,
} from '@/models/CatalogueFilters';

/**
 * Converts a custom field key into a human-readable label.
 * e.g. "material_thickness" → "Material Thickness"
 */
const toLabel = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Maps a CatalogueCustomFieldMeta to a CatalogueFilterDefinition
 */
const toFilterDefinition = (
  meta: CatalogueCustomFieldMeta,
): CatalogueFilterDefinition => {
  const cfKey = `cf.${meta.key}`;

  if (meta.fieldType === 'integer' || meta.fieldType === 'float') {
    return {
      key: cfKey,
      label: toLabel(meta.key),
      type: CatalogueFilterType.NUMBER,
      icon: meta.fieldType === 'integer' ? 'mdi-numeric' : 'mdi-decimal',
      placeholder: '0',
      field: cfKey,
      defaultOp: CatalogueFilterOp.EQ,
      availableOps: NUMBER_OPS,
      group: CatalogueFilterGroup.CUSTOM_FIELDS,
    };
  }

  // Default: string
  return {
    key: cfKey,
    label: toLabel(meta.key),
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-format-text',
    placeholder: `Search ${toLabel(meta.key).toLowerCase()}...`,
    field: cfKey,
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.CUSTOM_FIELDS,
  };
};

/**
 * Composable for loading and managing custom field filter definitions.
 * Fetches available custom fields from the backend and converts them
 * to CatalogueFilterDefinition objects that can be merged into the filter bar.
 */
export const useCatalogueCustomFields = () => {
  const customFieldMeta = ref<CatalogueCustomFieldMeta[]>([]);
  const customFieldFilters = ref<CatalogueFilterDefinition[]>([]);
  const isLoading = ref(false);

  const loadCustomFields = async () => {
    isLoading.value = true;
    try {
      const meta = await getCatalogueItemCustomFields();
      customFieldMeta.value = meta;
      customFieldFilters.value = meta.map(toFilterDefinition);
    } catch (error) {
      console.error('Failed to load custom field definitions:', error);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    customFieldMeta,
    customFieldFilters,
    isLoading,
    loadCustomFields,
  };
};
